// ============================================================
// Abonnements & Contrats — centre de pilotage (frontend uniquement)
// Données : GET /clients (paginé) + GET /clients/:id/contrats par client
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";

import {
  AdminPageHero,
  AdminDataTableShell,
  adminHeroActionClassName,
} from "../../modules/admin/ui";
import { api } from "../../lib/axios";
import {
  buildSubscriptionRow,
  expiresWithinDays,
  fetchAllClients,
  fetchContratsByClientIds,
  isCalendarExpired,
  parseDecimal,
  type ClientApi,
  type ContratApi,
  type SubscriptionAlertItem,
  SubscriptionAlertsPanel,
  SubscriptionEmptyState,
  SubscriptionFilters,
  SubscriptionKpiGrid,
  SubscriptionTable,
} from "../../modules/admin/subscriptions";

type ContratClientPair = { contrat: ContratApi; client: ClientApi };

function buildPairs(clients: ClientApi[], byClient: Map<number, ContratApi[]>): ContratClientPair[] {
  const pairs: ContratClientPair[] = [];
  for (const cl of clients) {
    const list = byClient.get(cl.id) ?? [];
    for (const contrat of list) {
      pairs.push({ contrat, client: cl });
    }
  }
  return pairs;
}

function buildAlerts(clients: ClientApi[], pairs: ContratClientPair[]): SubscriptionAlertItem[] {
  const clientsWithActif = new Set(
    pairs.filter((p) => p.contrat.statut === "ACTIF").map((p) => p.client.id)
  );
  const sansAbonnement = clients.filter((c) => !clientsWithActif.has(c.id)).length;

  const contratsExpires = pairs.filter(
    (p) =>
      p.contrat.statut === "EXPIRE" ||
      p.contrat.statut === "TERMINE" ||
      isCalendarExpired(p.contrat.dateFin)
  ).length;

  const expireBientot = pairs.filter(
    (p) => p.contrat.statut === "ACTIF" && expiresWithinDays(p.contrat.dateFin, 30)
  ).length;

  const sansDotation = pairs.filter(
    (p) =>
      p.contrat.statut === "ACTIF" &&
      (p.client.nombreBacs == null || p.client.nombreBacs <= 0) &&
      !p.client.volumeDechets?.trim()
  ).length;

  const sansProchainPassage = pairs.filter(
    (p) => p.contrat.statut === "ACTIF" && p.contrat.prochainPassage == null
  ).length;

  const clientsDette = clients.filter((c) => parseDecimal(c.soldeClient) > 0).length;

  return [
    {
      id: "sans-abo",
      label: "Clients sans abonnement actif",
      count: sansAbonnement,
      level: "WARNING",
      action: "Créer ou réactiver un contrat depuis la fiche client ou le flux d’onboarding.",
    },
    {
      id: "contrats-expires",
      label: "Contrats expirés ou terminés",
      count: contratsExpires,
      level: "CRITICAL",
      action: "Renouveler, clôturer ou archiver les dossiers concernés.",
    },
    {
      id: "expire-bientot",
      label: "Contrats actifs expirant sous 30 jours",
      count: expireBientot,
      level: "WARNING",
      action: "Anticiper renouvellement et passage commercial.",
    },
    {
      id: "sans-dotation",
      label: "Abonnements actifs sans dotation renseignée (bacs / volume)",
      count: sansDotation,
      level: "INFO",
      action: "Compléter les équipements sur la fiche client pour fiabiliser la logistique.",
    },
    {
      id: "sans-passage",
      label: "Abonnements actifs sans prochain passage planifié",
      count: sansProchainPassage,
      level: "WARNING",
      action: "Brancher la planification terrain ou renseigner les créneaux côté contrat.",
    },
    {
      id: "dette",
      label: "Clients avec solde débiteur",
      count: clientsDette,
      level: "CRITICAL",
      action: "Relancer depuis Recouvrement ou régulariser les paiements.",
    },
  ];
}

export default function ContratsPage(): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [clients, setClients] = useState<ClientApi[]>([]);
  const [pairs, setPairs] = useState<ContratClientPair[]>([]);

  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterFrequence, setFilterFrequence] = useState("");
  const [filterTypeClient, setFilterTypeClient] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [impayesOnly, setImpayesOnly] = useState(false);
  const [expireBientotOnly, setExpireBientotOnly] = useState(false);
  const [generatingMonthlyInvoices, setGeneratingMonthlyInvoices] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const initialClients = await fetchAllClients();
      const byClient = await fetchContratsByClientIds(initialClients.map((c) => c.id));
      const allClients = await fetchAllClients();
      const builtPairs = buildPairs(allClients, byClient);
      setClients(allClients);
      setPairs(builtPairs);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des abonnements et contrats.");
      setClients([]);
      setPairs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const rowsAll = useMemo(
    () => pairs.map((p) => buildSubscriptionRow(p.contrat, p.client)),
    [pairs]
  );

  const kpis = useMemo(() => {
    const actifs = pairs.filter((p) => p.contrat.statut === "ACTIF").length;

    const clientsWithActif = new Set(
      pairs.filter((p) => p.contrat.statut === "ACTIF").map((p) => p.client.id)
    );
    const clientsSansAbonnement = clients.filter((c) => !clientsWithActif.has(c.id)).length;

    const sansPlanning = pairs.filter(
      (p) =>
        p.contrat.statut === "ACTIF" &&
        p.contrat.prochainPassage == null &&
        (p.contrat.nbPassagesPrevus == null || p.contrat.nbPassagesPrevus === 0)
    ).length;

    const impayesLie = pairs.filter(
      (p) => p.contrat.statut === "ACTIF" && parseDecimal(p.client.soldeClient) > 0
    ).length;

    const mensuels = pairs.filter((p) => p.contrat.statut === "ACTIF" && p.contrat.frequence === "MENSUEL");
    const revenuMensuel =
      mensuels.length > 0
        ? mensuels.reduce((s, p) => s + parseDecimal(p.contrat.montant), 0)
        : null;

    const revenuMensuelLabel =
      revenuMensuel != null && revenuMensuel > 0
        ? `${revenuMensuel.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} USD`
        : "—";

    const revenuMensuelHint =
      mensuels.length === 0
        ? "À connecter — aucun contrat actif à fréquence mensuelle ; pas d’estimation agrégée hors mensuel."
        : "Somme des montants des contrats actifs à fréquence mensuelle (hors autres fréquences).";

    return {
      actifs,
      clientsSansAbonnement,
      sansPlanning,
      impayesLie,
      revenuMensuelLabel,
      revenuMensuelHint,
    };
  }, [pairs, clients]);

  const alerts = useMemo(() => buildAlerts(clients, pairs), [clients, pairs]);

  const zoneOptions = useMemo(() => {
    const z = new Set<string>();
    for (const r of rowsAll) {
      if (r.zoneLabel && r.zoneLabel !== "—") z.add(r.zoneLabel);
    }
    return [...z].sort((a, b) => a.localeCompare(b, "fr"));
  }, [rowsAll]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rowsAll.filter((r) => {
      if (filterStatut && r.statutContrat !== filterStatut) return false;
      if (filterFrequence && r.frequence !== filterFrequence) return false;
      if (filterTypeClient && r.clientType !== filterTypeClient) return false;
      if (filterZone && r.zoneLabel !== filterZone) return false;
      if (impayesOnly && !r.hasDebt) return false;
      if (expireBientotOnly && !r.expireBientot) return false;
      if (q && !r.clientDisplay.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rowsAll, search, filterStatut, filterFrequence, filterTypeClient, filterZone, impayesOnly, expireBientotOnly]);

  const wireSoon = (): void => {
    toast.info("Module à brancher — disponible après branchement métier.", {
      position: "bottom-center",
    });
  };

  const handleGenerateMonthlyInvoices = async (): Promise<void> => {
    setGeneratingMonthlyInvoices(true);
    try {
      const { data } = await api.post<{ message?: string; created?: number; existing?: number }>(
        "/factures/abonnements/mensuelles"
      );
      const created = Number(data?.created ?? 0);
      const existing = Number(data?.existing ?? 0);
      toast.success(
        data?.message ??
          (created > 0
            ? `${created} facture(s) mensuelle(s) créée(s).`
            : `Aucune facture manquante (${existing} déjà existante(s)).`)
      );
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Impossible de générer les factures mensuelles.");
    } finally {
      setGeneratingMonthlyInvoices(false);
    }
  };

  const heroActions = (
    <>
      <button
        type="button"
        onClick={() => wireSoon()}
        className={adminHeroActionClassName(false)}
      >
        Nouvel abonnement
      </button>
      <button
        type="button"
        onClick={() => void handleGenerateMonthlyInvoices()}
        disabled={generatingMonthlyInvoices}
        className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        {generatingMonthlyInvoices ? "Génération..." : "Générer factures mensuelles"}
      </button>
      <Link
        to="/admin/recouvrement"
        className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        Relancer impayés
      </Link>
      <Link
        to="/admin/planificateur"
        className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
      >
        Voir planning global
      </Link>
    </>
  );

  const handleRefresh = (): void => setRefreshToken((x) => x + 1);

  const totalContrats = pairs.length;

  return (
    <div className="space-y-5 text-slate-100">
      <AdminPageHero
        eyebrow="Pilotage commercial"
        title="Abonnements & Contrats"
        subtitle="Suivi des plans clients, dotations, passages et facturation."
        actions={heroActions}
      />

      <SubscriptionKpiGrid
        abonnementsActifs={kpis.actifs}
        clientsSansAbonnement={kpis.clientsSansAbonnement}
        sansPlanning={kpis.sansPlanning}
        impayesAbonnements={kpis.impayesLie}
        revenuMensuelLabel={kpis.revenuMensuelLabel}
        revenuMensuelHint={kpis.revenuMensuelHint}
      />

      <SubscriptionAlertsPanel items={alerts} />

      <SubscriptionFilters
        search={search}
        onSearch={setSearch}
        filterStatut={filterStatut}
        onStatut={setFilterStatut}
        filterFrequence={filterFrequence}
        onFrequence={setFilterFrequence}
        filterTypeClient={filterTypeClient}
        onTypeClient={setFilterTypeClient}
        filterZone={filterZone}
        onZone={setFilterZone}
        zoneOptions={zoneOptions}
        impayesOnly={impayesOnly}
        onImpayesOnly={setImpayesOnly}
        expireBientotOnly={expireBientotOnly}
        onExpireBientotOnly={setExpireBientotOnly}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
        >
          <RefreshCcw className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {loading ? (
        <p className="rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-center text-sm text-slate-400">
          Chargement des abonnements et contrats…
        </p>
      ) : totalContrats === 0 ? (
        <AdminDataTableShell>
          <SubscriptionEmptyState />
        </AdminDataTableShell>
      ) : filteredRows.length === 0 ? (
        <AdminDataTableShell>
          <div className="px-6 py-12 text-center text-sm text-slate-400">
            Aucun résultat pour ces filtres. Réinitialisez la recherche ou les critères.
          </div>
        </AdminDataTableShell>
      ) : (
        <SubscriptionTable rows={filteredRows} />
      )}
    </div>
  );
}
